import { createEffect, onCleanup, onMount } from "solid-js"
import { createStore } from "solid-js/store"
import { useLanguage } from "@/context/language"
import "@/cowork/report-workspace.css"

export function ReportHtmlPreview(props: { src?: string; srcdoc?: string; title?: string }) {
  const language = useLanguage()
  let frame: HTMLIFrameElement | undefined
  let viewport: HTMLDivElement | undefined
  let documentObserver: ResizeObserver | undefined
  const [state, setState] = createStore({ height: 900, width: 1024, viewportWidth: 1024, viewportHeight: 700, scale: 1, mode: "width" })
  const scale = () => state.mode === "width" ? Math.min(1, (state.viewportWidth - 32) / state.width)
    : state.mode === "page" ? Math.min(1, (state.viewportWidth - 32) / state.width, (state.viewportHeight - 32) / state.height) : state.scale
  createEffect(() => { props.src; props.srcdoc; setState({ height: 900, width: 1024, mode: "width" }) })
  const resize = () => {
    const doc = frame?.contentDocument
    if (!doc) return
    setState({ height: Math.max(900, doc.body?.scrollHeight ?? 0, doc.documentElement.scrollHeight),
      width: Math.max(1024, doc.body?.scrollWidth ?? 0, doc.documentElement.scrollWidth) })
  }
  onMount(() => {
    if (!viewport) return
    const observer = new ResizeObserver(() => setState({ viewportWidth: Math.max(100, viewport!.clientWidth), viewportHeight: Math.max(100, viewport!.clientHeight) }))
    observer.observe(viewport)
    onCleanup(() => observer.disconnect())
  })
  onCleanup(() => documentObserver?.disconnect())
  const zoom = (amount: number) => setState({ mode: "manual", scale: Math.max(.1, Math.min(2, Math.round((scale() + amount) * 100) / 100)) })
  return <div class="flex h-full min-h-[480px] flex-col overflow-hidden bg-v2-background-bg-base">
    <div class="report-preview-toolbar">
      <span class="text-12-regular">{language.t("cowork.pipeline.report.previewScale", { scale: `${Math.round(scale() * 100)}%` })}</span>
      <div class="flex flex-wrap gap-1">
        <button class="report-workspace-action" aria-pressed={state.mode === "width"} onClick={() => setState("mode", "width")}>{language.t("cowork.report.workspace.fitWidth")}</button>
        <button class="report-workspace-action" aria-pressed={state.mode === "page"} onClick={() => setState("mode", "page")}>{language.t("cowork.report.workspace.fitPage")}</button>
        <button class="report-workspace-action" onClick={() => setState({ mode: "manual", scale: 1 })}>{language.t("cowork.report.workspace.actualSize")}</button>
        <button class="report-workspace-action" disabled={scale() <= .1} aria-label={language.t("cowork.pipeline.report.zoomOut")} onClick={() => zoom(-.1)}>−</button>
        <button class="report-workspace-action" disabled={scale() >= 2} aria-label={language.t("cowork.pipeline.report.zoomIn")} onClick={() => zoom(.1)}>+</button>
      </div>
    </div>
    <div ref={viewport} class="min-h-0 flex-1 overflow-auto bg-v2-background-bg-layer-01 p-4">
      <div class="mx-auto shadow-sm" style={{ width: `${state.width * scale()}px`, height: `${state.height * scale()}px` }}>
        <iframe ref={frame} title={props.title || language.t("cowork.pipeline.report")} src={props.src} srcdoc={props.srcdoc}
          sandbox="allow-same-origin" referrerpolicy="no-referrer"
          class="origin-top-left border-0 bg-white" style={{ width: `${state.width}px`, height: `${state.height}px`, transform: `scale(${scale()})` }}
          onLoad={() => {
            documentObserver?.disconnect()
            resize()
            const body = frame?.contentDocument?.body
            if (!body) return
            documentObserver = new ResizeObserver(resize)
            documentObserver.observe(body)
          }} />
      </div>
    </div>
  </div>
}
